import { segmentedItemVariants, segmentedShellVariants } from '@/components/ui/button-variants';
import { useId, type ReactNode } from 'react';

export type TabItem = {
  id: string;
  label: string;
  panel: ReactNode;
};

type TabsProps = {
  tabs: TabItem[];
  activeId: string;
  onChange: (id: string) => void;
  className?: string;
  /** Width constraint for the tab strip only (panel stays full width of parent). */
  tabListClassName?: string;
  panelClassName?: string;
};

export function Tabs({
  tabs,
  activeId,
  onChange,
  className,
  tabListClassName,
  panelClassName
}: TabsProps) {
  const baseId = useId();
  const active = tabs.find((t) => t.id === activeId) ?? tabs[0];

  return (
    <div className={className}>
      <div role="tablist" className={segmentedShellVariants(tabListClassName)}>
        {tabs.map((tab) => {
          const selected = tab.id === activeId;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`${baseId}-${tab.id}`}
              aria-selected={selected}
              aria-controls={`${baseId}-panel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              className={segmentedItemVariants({ selected })}
              onClick={() => onChange(tab.id)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <div
        role="tabpanel"
        id={`${baseId}-panel-${active?.id}`}
        aria-labelledby={`${baseId}-${active?.id}`}
        className={panelClassName ?? 'mt-4 min-w-0'}
      >
        {active?.panel}
      </div>
    </div>
  );
}
